import { Body, Box, Button, Flex, IconButton } from '@hover/blueprint';
import type { FlexProps } from '@hover/blueprint/chakra';
import { iMoreHorizontal } from '@hover/icons';
import React, { useMemo, useState } from 'react';

import { ImageWithTint } from 'src/modules/designStudio/components/DesignMenu/ProductCatalogMenu/ImageWithTint';
import { TestLabels } from 'src/modules/designStudio/components/DesignMenu/ProductCatalogMenu/ProductSearch/TestIds';
import { getTexturedAppearanceColorGroup } from 'src/modules/designStudio/components/DesignMenu/ProductCatalogMenu/materialGroupUtils';
import { ColorGroupNames } from 'src/modules/designStudio/helpers/productSelection/VariationColorGroups';
import type { MaterialGroup } from 'src/modules/designStudio/helpers/productSelection/types';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';

export interface VariationGroupCardParams extends FlexProps {
  group: MaterialGroup;
  onPrimaryClick: () => void;
  onSecondaryClick: () => void;
}

export const VariationGroupCard = ({
  group,
  onPrimaryClick,
  onSecondaryClick,
}: VariationGroupCardParams) => {
  const t = useTranslate(TranslationNamespace.DESIGN_STUDIO);
  const variation = group.variation!;

  const [isHovered, setIsHovered] = useState(false);
  const appearanceMatchesVariationColor =
    group.appearance.color === variation.color?.hex;

  const displayColorName: string = useMemo(() => {
    const appearanceColorGroup = getTexturedAppearanceColorGroup(
      group.appearance,
    );
    if (appearanceMatchesVariationColor) {
      return variation.name;
    }
    return appearanceColorGroup ? ColorGroupNames[appearanceColorGroup] : '';
  }, [group, variation, appearanceMatchesVariationColor]);

  return (
    <Flex
      _hover={{
        outlineStyle: 'solid',
        outlineThickness: '2px',
        outlineColor: 'neutral.300',
        bg: 'white',
      }}
      bg="neutral.100"
      borderRadius={450}
      direction="column"
      gap={0}
      maxWidth="156px"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      width="100%"
    >
      <Box
        borderBottomLeftRadius={0}
        borderBottomRightRadius={0}
        borderTopLeftRadius={450}
        borderTopRightRadius={450}
        position="relative"
        width="100%"
      >
        <ImageWithTint
          borderBottomLeftRadius={0}
          borderBottomRightRadius={0}
          borderTopLeftRadius={450}
          borderTopRightRadius={450}
          color={group.appearance.color ?? 'neutral.0'}
          height="110px"
          thumbnail={variation.thumbnailURL!}
        />
        {appearanceMatchesVariationColor && (
          /**
           * The group.appearance.color and variation.color don't match when the variation was applied through a palette / template.
           * In this situation we don't want to show the details button because the variation that will be pulled up if they do click it will show the wrong color.
           * Consider the following situation. The user applied a template and one of the wall groups appearts black. In reality the
           * variation associated with that wall group is white, and we are simplt coloring black on top of it. In this case if the user clicks the details view,
           * it'll open up the white variation. This check is meant to avoid this.
           */
          <IconButton
            aria-label={TestLabels.WALL_GROUP_VARIATION_DETAILS}
            bg="#03030364"
            height={300}
            icon={iMoreHorizontal}
            iconSize="medium"
            label={TestLabels.WALL_GROUP_VARIATION_DETAILS}
            onClick={onSecondaryClick}
            position="absolute"
            right={200}
            size="small"
            top={200}
            width={300}
          />
        )}
      </Box>
      <Flex
        alignItems="start"
        bg="clear"
        borderBottomLeftRadius="20px"
        borderBottomRightRadius="20px"
        direction="column"
        gap={0}
        p="16px"
      >
        <Body fontSize="15px" fontWeight={700} lineHeight="18px" my={0}>
          {variation.product.provider.name}
        </Body>
        <Body
          color="neutral.600"
          fontSize="15px"
          fontWeight={400}
          lineHeight="18.75px"
          my={0}
        >
          {displayColorName}
        </Body>

        <Button
          aria-label={TestLabels.WALL_GROUP_VARIATION_EDIT}
          colorScheme={isHovered ? 'dark' : 'light'}
          label={TestLabels.WALL_GROUP_VARIATION_EDIT}
          marginTop="8px"
          onClick={onPrimaryClick}
          size="small"
          variant={isHovered ? 'glass' : 'primary'}
          width="100%"
        >
          {t('productCatalogMenu.productDetails.edit')}
        </Button>
      </Flex>
    </Flex>
  );
};
